import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

// Phase 12 Cleanup: Die Legacy-Checklist-Config `EMPLOYEE_TUTORIAL_CONFIG:
// ChecklistStep[]` und die `registerEmployeeTutorials(registry)`-Funktion
// wurden entfernt. Beide gehoerten zum alten `OnboardingChecklistService`-
// Flow und werden nicht in die V2-Plattform migriert (ADR E4). Aktive
// Tour-Definitionen leben unten als `TourConfig`.

/**
 * Welcome-Tour V4 (kompakt): ersten Mitarbeiter anlegen in 7 Section-Steps
 * mit Voice-Over und synchronem Cue-Highlighting (analog zur Skills- und
 * Staffing-Management-Tour).
 *
 * Statt feldgranularer Steps gibt es pro Form-Sektion einen Erklaer-Step,
 * der die ganze Sektion umrahmt (Spotlight auf <mat-expansion-panel>) und
 * waehrend des Voice-Overs maximal drei zentrale Felder per Pulse-Cue
 * anhebt — der Spotlight-Ring bleibt ruhig auf der Sektion, die Cues
 * lenken die Aufmerksamkeit zu den jeweils erwaehnten Feldern, ohne dass
 * der Hauptrahmen springt:
 *   1. Mitarbeiter hinzufügen — Add-Button klicken
 *   2. Persönliche Informationen (Cues: Vorname, Nachname)
 *   3. Kontakt & Einladung (Cue: E-Mail-Adresse)
 *   4. Anstellungsdetails (Cues: Rolle, Abteilung, Startdatum)
 *   5. Arbeitszeit & Urlaub (Cues: Beschaeftigungsart, Arbeitstage,
 *      Arbeitsstunden)
 *   6. Speichern — Spotlight am echten Save-Button
 *   7. Abschluss-Hinweis — PIN/Einladung als Folge-Thema
 *
 * Optionale Felder (Geschlecht, Geburtsdatum, Mobilnummer, Vorgesetzter,
 * Personalnummer, Urlaubstage etc.) werden im Voice-Over erwaehnt aber
 * NICHT gepulst — das haelt den Spotlight ruhig und vermeidet das
 * Hin-und-her-Springen, das bei feingranularer Cue-Liste entstanden waere.
 *
 * Version 4: Konsolidierung 11 → 7 Steps; Bestandskunden mit V3-Replay-
 * State sehen V4 als Replay-Angebot (statePolicy.allowReplay).
 *
 * Cue-Daten leben pro Step in `src/assets/onboarding/audio/<lang>/
 * first-employee/<stepId>.cues.json` (vom generate-voiceover-Script
 * geschrieben). Der Coordinator setzt `hasCues=true` zur Laufzeit aus dem
 * Audio-Manifest.
 */
export const FIRST_EMPLOYEE_ONBOARDING_TOUR: TourConfig = {
  id: TourFeature.FirstEmployee,
  version: 4,
  title: 'CHECKLIST.FIRST_EMPLOYEE.TITLE',
  description: 'CHECKLIST.FIRST_EMPLOYEE.DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  // Tour fuehrt durch den Mitarbeiter-Anlegen-Wizard. Ohne CanCreateEmployees
  // wird der Wizard im UI ausgeblendet — wir zeigen den Schritt analog aus.
  requiredPermissions: [['Employee.CanCreateEmployees']],
  triggers: [
    TourTrigger.FirstVisit,
    TourTrigger.Resume,
    TourTrigger.Manual,
    TourTrigger.AfterSuccess,
  ],
  // AfterSuccess: wird als Nudge angeboten, sobald der User die erste
  // Qualifikation angelegt hat. `department.created` darf hier NICHT stehen,
  // weil sonst nach jedem Department gleichzeitig `add-skills` (priority 30)
  // und `first-employee` (priority 40) als Kandidaten matchen — der Nudge
  // springt dann auf das falsche Ziel und der Funnel wird inkonsistent.
  // Sequenz im Setup: Department -> Skill -> Mitarbeiter.
  triggerContext: {
    route: '/employee',
    requiresSuccessOf: 'skill.created',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: true,
    cooldownHoursAfterDismiss: 168,
    cooldownHoursAfterSnooze: 24,
    suppressAfterCompletion: true,
  },
  priority: 40,
  estimatedDurationSec: 300,
  category: 'setup',
  // Strikte Sequenz: das form-getriebene Onboarding muss durch alle 7 Steps
  // laufen. Bisheriges Verhalten erlaubte stilles Skipping (z. B. bei
  // erfolgreichem Save direkt zu save-employee gesprungen, ohne
  // contact/employment/worktime gesehen zu haben). Mit diesem Flag bleibt
  // die Tour auf dem aktuellen Step stehen, bis der User explizit Weiter
  // klickt; bei Form-Save wird KEIN Tour-Advance ausgeloest.
  strictSequence: true,
  chapters: [
    {
      id: 'main',
      title: 'CHECKLIST.FIRST_EMPLOYEE.TITLE',
      steps: [
        {
          id: 'add-employee',
          anchor: AnchorId.AddEmployeeButton,
          title: 'TUTORIAL.EMPLOYEE.V2.ADD.TITLE',
          content: 'TUTORIAL.EMPLOYEE.V2.ADD.CONTENT',
          placement: 'bottom',
          nextCondition: 'onClick',
          showNextButton: false,
        },
        {
          // Section-Step Personal: Spotlight auf das ganze Panel,
          // Voice-Cues pulsen Vorname und Nachname. Personal-Panel ist
          // per [expanded]="true" beim Dialog-Mount bereits offen;
          // panelIndex 0 + waitForOpen verifizieren das fuer den Runner.
          // requiredAnchors blockt Weiter, bis Vor- und Nachname (beide
          // Save-Blocker des Form-Models) gefuellt sind. Macht den
          // Pflichtcharakter sofort sichtbar, ohne fuer jedes Feld einen
          // eigenen Step zu brauchen.
          id: 'personal-section',
          anchor: AnchorId.EmployeePersonalSection,
          spotlightAnchor: AnchorId.EmployeePersonalSection,
          requiredAnchors: [
            AnchorId.EmployeeFirstName,
            AnchorId.EmployeeLastName,
          ],
          title: 'TUTORIAL.EMPLOYEE.V2.PERSONAL.TITLE',
          content: 'TUTORIAL.EMPLOYEE.V2.PERSONAL.CONTENT',
          placement: 'right',
          panelIndex: 0,
          // Erster Step im offenen Add-Dialog — Vorgaenger zeigt den
          // "+ Hinzufuegen"-Button auf Page-Ebene, der jetzt vom Dialog
          // verdeckt ist. Zurueck-Sprung waere nicht mehr sichtbar.
          showPrevButton: false,
          // preserveOnClose: Tooltip-X / Esc pausiert NUR die Tour — der
          // Mitarbeiter-Dialog bleibt offen, damit der User seine bereits
          // getippten Daten (Vor-/Nachname etc.) nicht verliert. Ohne das Flag
          // wuerde der Coordinator beim Pause-Outcome `closeAllOpenDialogs()`
          // aufrufen (setup-Kategorie). Spiegelt department-/skills-tutorial.
          // Folge fuer Resume: Der Dialog bleibt offen → beim „Fortsetzen" aus
          // der Checkliste ist `hasAnyDialog=true`, der Ziel-Step gilt als
          // renderbar und die Tour setzt exakt am pausierten Step im offenen
          // Dialog fort, statt rueckwaerts auf den Add-Button zu springen und
          // einen leeren Dialog neu zu oeffnen.
          dialogContext: { waitForOpen: true, attachToDialog: true, preserveOnClose: true },
        },
        {
          // Section-Step Kontakt: Spotlight auf das Panel, Cue auf
          // E-Mail-Feld als Anker fuer die Einladungs-Mechanik.
          id: 'contact-section',
          anchor: AnchorId.EmployeeContactSection,
          spotlightAnchor: AnchorId.EmployeeContactSection,
          title: 'TUTORIAL.EMPLOYEE.V2.CONTACT.TITLE',
          content: 'TUTORIAL.EMPLOYEE.V2.CONTACT.CONTENT',
          placement: 'right',
          panelIndex: 1,
          // preserveOnClose: Tour-X pausiert nur, Dialog bleibt offen +
          // Resume setzt hier fort (siehe personal-section).
          dialogContext: { attachToDialog: true, preserveOnClose: true },
        },
        {
          // Section-Step Anstellung: Spotlight auf Panel, Cues auf die drei
          // Pflichtfelder Rolle, Abteilung und Startdatum. Optionale Felder
          // (Skills, Vorgesetzter, Personalnummer) werden nur im Voice-Over
          // erwaehnt, nicht gepulst — vermeidet Spotlight-Springen.
          id: 'employment-section',
          anchor: AnchorId.EmployeeEmploymentSection,
          spotlightAnchor: AnchorId.EmployeeEmploymentSection,
          title: 'TUTORIAL.EMPLOYEE.V2.EMPLOYMENT.TITLE',
          content: 'TUTORIAL.EMPLOYEE.V2.EMPLOYMENT.CONTENT',
          placement: 'right',
          panelIndex: 2,
          // preserveOnClose: Tour-X pausiert nur, Dialog bleibt offen +
          // Resume setzt hier fort (siehe personal-section).
          dialogContext: { attachToDialog: true, preserveOnClose: true },
        },
        {
          // Section-Step Arbeitszeit: Cues auf Beschaeftigungsart und
          // Arbeitszeit-Modell. WorkDays/WorkHours sind je nach JobType
          // bedingt im DOM (Minijob z. B. blendet sie aus) — der Cue-Driver
          // ignoriert fehlende Anchors stillschweigend.
          id: 'worktime-section',
          anchor: AnchorId.EmployeeWorktimeSection,
          spotlightAnchor: AnchorId.EmployeeWorktimeSection,
          title: 'TUTORIAL.EMPLOYEE.V2.WORKTIME.TITLE',
          content: 'TUTORIAL.EMPLOYEE.V2.WORKTIME.CONTENT',
          placement: 'right',
          panelIndex: 3,
          // preserveOnClose: Tour-X pausiert nur, Dialog bleibt offen +
          // Resume setzt hier fort (siehe personal-section).
          dialogContext: { attachToDialog: true, preserveOnClose: true },
        },
        {
          // Spotlight auf den echten Save-Button. placement:'top'
          // positioniert den Popover oberhalb der Dialog-Action-Bar.
          //
          // Wir advancen NICHT per `onClick`, sondern warten auf das
          // `tour:employee-saved`-CustomEvent, das `add-employee.component`
          // erst nach erfolgreicher Backend-Persistierung feuert (vor
          // dialogRef.close()). Grund: Bei `onClick` wechselt die Tour
          // sofort zum naechsten Step (Drei-Punkte-Menue auf der Liste),
          // wodurch der MatDialog/die Komponente waehrend des laufenden
          // POSTs zerstoert wurde. Der HTTP-Request war an
          // `takeUntil(destroy$)` gekoppelt → Browser cancelte den Fetch,
          // der `next`-Block lief nie, die Liste wurde nicht refreshed
          // (Mitarbeiter erschien erst nach F5). Bei Validierungs- oder
          // Backend-Fehlern bleibt die Tour korrekt am Save-Step stehen,
          // weil das Event dann gar nicht erst gefeuert wird.
          id: 'save-employee',
          anchor: AnchorId.SaveEmployeeButton,
          title: 'TUTORIAL.EMPLOYEE.V2.SAVE.TITLE',
          content: 'TUTORIAL.EMPLOYEE.V2.SAVE.CONTENT',
          placement: 'top',
          advanceOnDocumentEvent: 'tour:employee-saved',
          showNextButton: false,
          // preserveOnClose: Tour-X pausiert nur, Dialog bleibt offen +
          // Resume setzt hier fort (siehe personal-section). Der normale
          // Save-Pfad ist unberuehrt — er advanced ueber
          // `tour:employee-saved` (Outcome=advance), nicht ueber den
          // Close-Pfad, in dem preserveOnClose wirkt.
          dialogContext: { attachToDialog: true, preserveOnClose: true },
        },
        {
          // Outro nach Speichern: Spotlight aufs Drei-Punkte-Menue der
          // ersten Mitarbeiterzeile (Anchor existiert je Zeile, Renderer
          // nimmt das erste sichtbare). Erklaert die Folge-Aktionen
          // (Einladung erneut versenden, PIN setzen, Deaktivieren), die
          // sonst niemand findet, wenn er sich nicht durch die Tabelle
          // klickt. Vorher zeigte der Step auf den Sidebar-Eintrag
          // "Mitarbeiter" — kein Bezug zum Voice-Over-Inhalt.
          id: 'completion-hint',
          anchor: AnchorId.EmployeeActionsMenu,
          title: 'TUTORIAL.EMPLOYEE.V2.COMPLETION.TITLE',
          content: 'TUTORIAL.EMPLOYEE.V2.COMPLETION.CONTENT',
          placement: 'left',
          // Save schliesst den Dialog und die Mitarbeiter-Liste re-rendert mit
          // dem neuen Eintrag. Ohne Wartezeit greift der Coordinator das
          // Anchor-Element bevor das Re-Render fertig ist — der Button-Knoten
          // ist dann als detached node im DOM und liefert ein 0x0-Rect, der
          // Spotlight bleibt unsichtbar. 600ms decken den Re-Render ab.
          onBeforeShow: async () => {
            await new Promise((r) => setTimeout(r, 600));
          },
          // Drei-Punkte-Icon ist nur 30x30 px — Padding hochziehen + halb-
          // runder Radius lassen den Spotlight-Ring deutlich sichtbar werden.
          spotlightPadding: 16,
          spotlightRadius: 24,
          showNextButton: true,
          // Page-Step nach Dialog-Close — Vorgaenger (Speichern-Button) lebte
          // im inzwischen geschlossenen Add-Dialog, Zurueck-Sprung waere ohne
          // Anchor.
          showPrevButton: false,
        },
      ],
    },
  ],
};

/**
 * Kontextuelle In-Dialog-Tour fuer "Mitarbeiter hinzufuegen". Wird via
 * Hilfe-Button im Dialog manuell gestartet (TourCoordinator.startTour mit
 * force=true).
 *
 * UX-Entscheidung (Phase 6 UX-Fix): Die Tour erklaert **nur Felder mit
 * nicht-offensichtlicher Semantik**. Selbsterklaerende Felder (Vorname,
 * Nachname, E-Mail, Adresse, Geschlecht, Profilbild) sind bewusst raus —
 * sie erhoehen Noise, aber keinen Lernwert.
 *
 * Drin bleiben: Pflichtfelder fuer Scheduling (Rolle, Abteilung, Vorgesetzter),
 * Vertrags- und Arbeitszeit-Felder mit Folgewirkung auf Schichtplanung und
 * DATEV-Lohn-Felder, die fachlich erklaert werden muessen.
 *
 * Fuer Text-Inputs ist `autoAdvance` via `blur` aktiv — der User muss nicht
 * bei jedem Feld manuell `Weiter` klicken.
 */

const AUTO_ADVANCE_ON_BLUR = {
  enabled: true,
  triggerOn: 'blur' as const,
  debounceMs: 300,
  minLength: 1,
};

export const EMPLOYEE_ADD_CONTEXTUAL_TOUR: TourConfig = {
  id: TourFeature.EmployeeAddContextual,
  version: 2,
  title: 'ADD_EMPLOYEE',
  description: 'EMPLOYEE_CONTEXTUAL_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 10,
  estimatedDurationSec: 120,
  category: 'contextual-help',
  chapters: [
    {
      id: 'contact',
      title: 'EMPLOYEE_FORM.CONTACT.TITLE',
      steps: [
        {
          id: 'email',
          anchor: AnchorId.EmployeeEmail,
          title: 'Email',
          content: 'EMAIL_ONBOARDING_DESCRIPTION',
          // bottom: paarig mit Send-PIN-Checkbox (Add) bzw. MobileNo (Edit)
          placement: 'bottom',
          panelIndex: 1,
          dialogContext: { attachToDialog: true },
          nextCondition: 'onInput',
          autoAdvance: AUTO_ADVANCE_ON_BLUR,
        },
      ],
    },
    {
      id: 'employment',
      title: 'EMPLOYEE_FORM.EMPLOYMENT.TITLE',
      steps: [
        {
          id: 'role',
          anchor: AnchorId.EmployeeRole,
          title: 'Role',
          content: 'ROLE_ONBOARDING_DESCRIPTION',
          // bottom: Select, paarig mit Department; vermeidet Dropdown-Overlap
          placement: 'bottom',
          panelIndex: 2,
          nextCondition: 'onChange',
          dialogContext: { attachToDialog: true },
        },
        {
          id: 'department',
          anchor: AnchorId.EmployeeDepartment,
          title: 'DEPARTMENT',
          content: 'DEPARTMENT_ONBOARDING_DESCRIPTION',
          // bottom: Multi-Select, paarig mit Role
          placement: 'bottom',
          panelIndex: 2,
          nextCondition: 'onChange',
          dialogContext: { attachToDialog: true },
        },
        {
          id: 'supervisor',
          anchor: AnchorId.EmployeeSupervisor,
          title: 'Supervisor',
          content: 'SUPERVISOR_ONBOARDING_DESCRIPTION',
          // bottom: Select, paarig mit PersonalNumber
          placement: 'bottom',
          panelIndex: 2,
          dialogContext: { attachToDialog: true },
          nextCondition: 'onChange',
        },
        {
          id: 'start-date',
          anchor: AnchorId.EmployeeStartDate,
          title: 'EMPLOYMENT START DATE',
          content: 'START_DATE_ONBOARDING_DESCRIPTION',
          // right: StartDate liegt allein in eigener Row; rechts ist Platz
          placement: 'right',
          panelIndex: 2,
          dialogContext: { attachToDialog: true },
          nextCondition: 'onInput',
          autoAdvance: AUTO_ADVANCE_ON_BLUR,
        },
        {
          id: 'contract-type',
          anchor: AnchorId.EmployeeContractType,
          title: 'Contract Type',
          content: 'CONTRACT_TYPE_ONBOARDING_DESCRIPTION',
          // bottom: Select, paarig mit ContractEndDate
          placement: 'bottom',
          panelIndex: 2,
          nextCondition: 'onChange',
          dialogContext: { attachToDialog: true },
        },
        {
          id: 'contract-end-date',
          anchor: AnchorId.EmployeeContractEndDate,
          title: 'Contract End Date',
          content: 'CONTRACT_END_DATE_ONBOARDING_DESCRIPTION',
          // bottom: paarig mit ContractType
          placement: 'bottom',
          panelIndex: 2,
          dialogContext: { attachToDialog: true },
          nextCondition: 'onInput',
          autoAdvance: AUTO_ADVANCE_ON_BLUR,
        },
      ],
    },
    {
      id: 'worktime',
      title: 'EMPLOYEE_FORM.WORKTIME.TITLE',
      steps: [
        {
          id: 'job-type',
          anchor: AnchorId.EmployeeJobType,
          title: 'TYPES OF EMPLOYMENT',
          content: 'JOB_TYPE_ONBOARDING_DESCRIPTION',
          // bottom: Select, paarig mit Holidays
          placement: 'bottom',
          panelIndex: 3,
          nextCondition: 'onChange',
          dialogContext: { attachToDialog: true },
        },
        {
          id: 'holidays',
          anchor: AnchorId.EmployeeHolidays,
          title: 'VACATION DAYS',
          content: 'HOLIDAYS_ONBOARDING_DESCRIPTION',
          // bottom: paarig mit JobType
          placement: 'bottom',
          panelIndex: 3,
          dialogContext: { attachToDialog: true },
          nextCondition: 'onInput',
          autoAdvance: AUTO_ADVANCE_ON_BLUR,
        },
        {
          id: 'work-days-per-week',
          anchor: AnchorId.EmployeeWorkDaysPerWeek,
          title: 'WORK DAYS PER WEEK',
          content: 'WORK_DAYS_PER_WEEK_ONBOARDING_DESCRIPTION',
          // bottom: Select, paarig mit WorkHoursPerDay
          placement: 'bottom',
          panelIndex: 3,
          dialogContext: { attachToDialog: true },
          nextCondition: 'onChange',
        },
        {
          id: 'work-hours-per-day',
          anchor: AnchorId.EmployeeWorkHoursPerDay,
          title: 'WEEKLY_WORK_HOURS',
          content: 'WEEKLY_WORK_HOURS_ONBOARDING_DESCRIPTION',
          // bottom: paarig mit WorkDaysPerWeek (Wochenarbeitszeit = Single Source of Truth)
          placement: 'bottom',
          panelIndex: 3,
          dialogContext: { attachToDialog: true },
          nextCondition: 'onInput',
          autoAdvance: AUTO_ADVANCE_ON_BLUR,
        },
      ],
    },
    {
      id: 'emergency',
      title: 'EMERGENCY_CONTACT',
      steps: [
        {
          id: 'emergency-contact-name',
          anchor: AnchorId.EmployeeEmergencyContactName,
          title: 'Emergency Contact Name',
          content: 'EMERGENCY_CONTACT_NAME_ONBOARDING_DESCRIPTION',
          // bottom: paarig mit Emergency-Phone
          placement: 'bottom',
          panelIndex: 4,
          dialogContext: { attachToDialog: true },
          nextCondition: 'onInput',
          autoAdvance: AUTO_ADVANCE_ON_BLUR,
        },
        {
          id: 'emergency-contact-phone',
          anchor: AnchorId.EmployeeEmergencyContactPhone,
          title: 'Emergency Contact Phone',
          content: 'EMERGENCY_CONTACT_PHONE_ONBOARDING_DESCRIPTION',
          // bottom: paarig mit Emergency-Name
          placement: 'bottom',
          panelIndex: 4,
          dialogContext: { attachToDialog: true },
          nextCondition: 'onInput',
          autoAdvance: AUTO_ADVANCE_ON_BLUR,
        },
      ],
    },
    {
      id: 'payroll',
      title: 'EMPLOYEE_FORM.PAYROLL.TITLE',
      steps: [
        {
          id: 'tax-id',
          anchor: AnchorId.EmployeeTaxId,
          title: 'EMPLOYEE_FORM.PAYROLL.TAX_ID_LABEL',
          content: 'EMPLOYEE_FORM.ONBOARDING.TAX_ID_DESC',
          // bottom: paarig mit SSN (col-6/col-6)
          placement: 'bottom',
          panelIndex: 5,
          dialogContext: { attachToDialog: true },
          nextCondition: 'onInput',
          autoAdvance: AUTO_ADVANCE_ON_BLUR,
        },
        {
          id: 'ssn',
          anchor: AnchorId.EmployeeSsn,
          title: 'EMPLOYEE_FORM.PAYROLL.SOCIAL_SECURITY_NUMBER_LABEL',
          content: 'EMPLOYEE_FORM.ONBOARDING.SSN_DESC',
          // bottom: paarig mit TaxId
          placement: 'bottom',
          panelIndex: 5,
          dialogContext: { attachToDialog: true },
          nextCondition: 'onInput',
          autoAdvance: AUTO_ADVANCE_ON_BLUR,
        },
        {
          id: 'tax-class',
          anchor: AnchorId.EmployeeTaxClass,
          title: 'EMPLOYEE_FORM.PAYROLL.TAX_CLASS_LABEL',
          content: 'EMPLOYEE_FORM.ONBOARDING.TAX_CLASS_DESC',
          // bottom: Select in 3-Col-Row mit ChildAllowance; Dropdown oeffnet nach unten
          placement: 'bottom',
          panelIndex: 5,
          nextCondition: 'onChange',
          dialogContext: { attachToDialog: true },
        },
      ],
    },
  ],
};
